/**
 * Copyright (c) 2023, CYBRA Corp. All rights reserved.
 */
import { Component, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { Action } from '../models/action';
import { EmGroup } from './../models/em-group';
import { GroupType } from './../models/group-type';

import { ActionDialogComponent } from './action-dialog.component';
import { ConfirmDeleteDialogComponent } from "./confirm-delete-dialog.component";
import { AddToGroupDialogComponent } from "./add-to-group-dialog.component";
import { ManageGroupsDialogComponent } from "./manage-groups-dialog.component";
import { RemoveFromGroupDialogComponent } from "./remove-from-group-dialog.component";
import { ShowGroupDialogComponent } from "./show-group-dialog.component";

import { ActionService } from '../services/action.service';
import { GroupService } from '../services/group.service';
import { ERROR_DIALOG_TITLE, SimpleSuccessErrorDialogComponent } from './simple-success-error-dialog-box/simple-success-error-dialog-box.component';

@Component({
  templateUrl: './actions.component.html'
})
export class ActionsComponent implements OnInit {
    
    @ViewChild('actionDialog')
    actionDialog: ActionDialogComponent;

    @ViewChild('confirmDeleteDialog')
    confirmDialog: ConfirmDeleteDialogComponent;
    
    @ViewChild('showGroupDialog')
    showGroupsDialog: ShowGroupDialogComponent;
    
    @ViewChild('addToGroupDialog')
    addToGroupDialogComponent: AddToGroupDialogComponent;
    
    @ViewChild('removeFromGroupDialog')
    removeFromGroupDialogComponent: RemoveFromGroupDialogComponent;
    
    @ViewChild('manageGroupsDialog')
    manageGroupsDialog: ManageGroupsDialogComponent;

    /** Reference to the SimpleSuccessErrorDialog used to display error */
  @ViewChild('successErrorDialog') 
  simpleSuccessErrorDialogComponent: SimpleSuccessErrorDialogComponent;
    
    action: Action;
    actions: ActionUI[] = [];

    constructor(
        private actionService: ActionService,
        private groupService: GroupService,
        private router: Router
    ) {
            
    }
    
    ngOnInit(): void {
        this.loadActions();
    }

    isEmptySelection(): boolean {
        return this.actions.filter(action => action.selected).length == 0;
    }
    
    isSingleSelection(): boolean {
        return this.actions.filter(action => action.selected).length == 1;
    }
    
    isMultipleSelection(): boolean {
        return this.actions.some(action => action.selected);
    }

    isAllSelected(): boolean {
        return this.actions.every(action => action.selected);
    }
    
    selectAll(event: any): void {
        this.actions.forEach(action => action.selected = event.target.checked);
    }

    loadActions(): void {
        const actionList: ActionUI[] = [];

        this.actionService.getAllActions().then(
            response => {
                for (const action of response) {
                    const actionUI = new ActionUI();
                    actionUI.action = action;
                    actionList.push(actionUI);  
                }
                
                this.actions = actionList;
            },
            error => {
                console.error('ActionsComponent: ', error);
            }
        );
    }
    
    onCreate(): void {
        this.action = new Action();
        this.actionDialog.show();
    }

    editAction(id: number) {
        //
        // retrieve action and pass to modal
        this.actionService.getActionById(id).then(
            response => {
                this.action = response;
                this.actionDialog.show();
            }
        );
    }

    createOrUpdateAction(): void {
        if (this.action.id == null) {
            this.actionService.createAction(this.action).then(
                response => {
                    this.loadActions();
                    this.actionDialog.hide();
                },
                error => { console.log("Error: " + error); }
            );
        }
        else {
            this.actionService.updateAction(this.action).then(
                response => {
                    this.loadActions();
                    this.actionDialog.hide();
                },
                error => { console.log("Error: " + error); }
            );
        }
    }

    deleteActions(): void {
        this.actionService.deleteActions(this.getSelectedActionIds()).then(
            response => {
                this.loadActions();
            },
            error => {
                this.handleError(error);
            }
        );
    }

    onEdit(): void {
        if (this.isSingleSelection()) {
            this.editAction(this.actions[0].action.id);
        }
    }

    onDelete(): void {
        if (!this.isEmptySelection()) {
            const names: string[] = [];
            this.actions.filter(ui => ui.selected).forEach(ui => names.push(ui.action.name));
            this.confirmDialog.show("Reaction", names);
        }
    }

    onAddGroups(): void {
         if (!this.isEmptySelection()) {
            this.addToGroupDialogComponent.show(GroupType.ACTION, this.getSelectedActionNames());
         }
    }
    
    addToGroup(group: EmGroup): void {
        this.groupService.addItemsToGroup(group.id, this.getSelectedActionIds()).then(
            success => {
                this.loadActions();
            },
            error => {
                this.handleError(error);
            }
        );
    }
    
    onRemoveGroup(): void {
        if (!this.isEmptySelection()) {
            const actionNames: string[] = [];
            const groupNames: string[] = [];
            const groups: EmGroup[] = [];
            
            this.getSelectedActions().forEach(
                action => {
                    actionNames.push(action.name);
                    
                    if (action.groups != null) {
                        action.groups.forEach(
                            group => {
                                if (!groupNames.includes(group.name)) {
                                    groupNames.push(group.name);
                                    groups.push(group);
                                }
                            }
                        );
                    }
                }
            );

            this.removeFromGroupDialogComponent.show(groups, actionNames);
        }
    }
    
    removeFromGroup(group: EmGroup): void {
        this.groupService.removeItemsFromGroup(group.id, this.getSelectedActionIds()).then(
            success => {
                this.loadActions();
            },
            error => {
                this.handleError(error);
            }
        );
    }
    
    onManageGroups(): void {
        this.manageGroupsDialog.show(GroupType.ACTION);
    }
    
    delete(): void {
        this.actions.filter(ui => ui.selected).forEach(
            ui => this.actionService.deleteAction(ui.action.id).then(
                response => {
                    console.log("Action deleted");
                },
                error => {
                    console.error('ActionsComponent: ', error);
                }
            )
        );

        this.loadActions();
    }

    getSelectedActions(): Action[] {
        const actions: Action[] = [];
        this.actions.filter(ui => ui.selected).forEach(ui => actions.push(ui.action));
        return actions;
    }
    
    getSelectedActionIds(): number[] {
        const ids: number[] = [];
        this.actions.filter(ui => ui.selected).forEach(ui => ids.push(ui.action.id));
        return ids;
    }

    getSelectedActionNames(): string[] {
        const names: string[] = [];
        this.actions.filter(ui => ui.selected).forEach(ui => names.push(ui.action.name));

        return names;
    }
    
    handleError(error: any): void {
        console.error("ActionsComponent.handleError() - Error: " + error);
        if (error != null) {
            // const response: Response = error;
            // Show the error in the SimpleSuccessErrorDialogComponent
          this.simpleSuccessErrorDialogComponent.showErrorDialog(ERROR_DIALOG_TITLE, error.error);
        }
    }
}

class ActionUI {
    selected = false;
    action: Action;
}
